import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { type FC, type ReactNode, useEffect, useRef, useState } from 'react';

import styles from '../styles.module.css';

export interface DescriptionPreviewProps {
  body: string;
  title?: string;
  titlePosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  maxHeight?: number;
  className?: string;
  /** Called when overflow status changes */
  onOverflowChange?: (isOverflowing: boolean) => void;
  /** Fullscreen modal to show when overflowing */
  children?: ReactNode;
}

/**
 * Shows a preview of the description, truncated to a max pixel height.
 * Notifies parent if overflow occurs.
 */
const DescriptionPreview: FC<DescriptionPreviewProps> = ({
  body,
  title,
  titlePosition = 'top-left',
  maxHeight = 300,
  className,
  onOverflowChange,
  children,
}) => {
  const previewRef = useRef<HTMLPreElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = previewRef.current;
    if (el) {
      const overflow = el.scrollHeight > el.clientHeight;
      setIsOverflowing(overflow);
      if (onOverflowChange) onOverflowChange(overflow);
    }
  }, [body, maxHeight, titlePosition, onOverflowChange]);

  const titleEl = title ? (
    <Text as="span" className={styles.title}>
      {title}
    </Text>
  ) : null;

  return (
    <div className={className}>
      {(titlePosition === 'top-left' || titlePosition === 'top-right') && titleEl}

      <pre className={styles.body} ref={previewRef} data-testid="description-preview">
        {body}
        {isOverflowing && children}
      </pre>

      {(titlePosition === 'bottom-left' || titlePosition === 'bottom-right') && titleEl}
    </div>
  );
};

export default DescriptionPreview;
